import { TypographyH4, TypographyMuted, TypographyXS } from "@/components/ui/typography"
import { useEffect, useState } from "react"
import { FileText, RefreshCcw } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import Position1Dashboard from "./components/Position1Dashboard"
import Position2Dashboard from "./components/Position2Dashboard"
import Position3Dashboard from "./components/Position3Dashboard"
import PositionTotalDashboard from "./components/PositionTotalDashboard"
import Button from "@/components/Button";
import { useProductTotalPositionQuery } from "@/queries/useUnsettledSecuritiesProductTotalPositionQuery";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'

import VirtualizedCommand from '@/components/VirtualizedCommand'
import ButtonWithTooltip from "@/components/ButtonWithTooltip";
import ViewPDF from "./viewPDF";
import useDisclosure from "@/hooks/useDisclosure";
import { usePositionDashBoardQuery } from "@/queries/usePositionDashBoardQuery";

const Dashboard = () => {

    const [selectDistributor, setSelectDistributor] = useState<string>()
    const [distributor, setDistributor] = useState<string[]>([])

    const [selectUFDistributor, setSelectUFDistributor] = useState<string[]>([])
    const [UFDistributor, setUFDistributor] = useState<string[]>([])

    const [selectRevenda, setSelectRevenda] = useState<string[]>([])
    const [revendasList, setRevendasList] = useState<Array<{ value: string, label: string }>>([])

    const [masterFilter, setMasterFilter] = useState<'nenhum' | 'congelados' | 'emAcordo' | 'juridico'>('nenhum')

    const { data, isFetching, refetch } = usePositionDashBoardQuery()
    const { data: dataProduct, refetch: refetchProduct } = useProductTotalPositionQuery()

    const getTopicBalance = (item: typeof data extends (infer T)[] | undefined ? T : never) => {
        if (masterFilter === 'congelados') {
            return (Number(item.VL_A_VENCER_CONGELADO_P1) || 0) + (Number(item.VL_VENCIDO_CONGELADO_P1) || 0) +
                (Number(item.VL_A_VENCER_CONGELADO_P2) || 0) + (Number(item.VL_VENCIDO_CONGELADO_P2) || 0) +
                (Number(item.VL_A_VENCER_CONGELADO_P3) || 0) + (Number(item.VL_VENCIDO_CONGELADO_P3) || 0)
        } else if (masterFilter === 'juridico') {
            return (Number(item.VL_A_VENCER_JURIDICO_P1) || 0) + (Number(item.VL_VENCIDO_JURIDICO_P1) || 0) +
                (Number(item.VL_A_VENCER_JURIDICO_P2) || 0) + (Number(item.VL_VENCIDO_JURIDICO_P2) || 0) +
                (Number(item.VL_A_VENCER_JURIDICO_P3) || 0) + (Number(item.VL_VENCIDO_JURIDICO_P3) || 0)
        } else if (masterFilter === 'emAcordo') {
            return item.TOTAL_EM_ABERTO_ACORDO || 0
        }
        return item.TOTAL_EM_ABERTO || 0
    }

    useEffect(() => {
        if (data) {
            const distributorsWithBalance = data.filter(item => getTopicBalance(item) > 0)
            const UniqueValues = Array.from(new Set(distributorsWithBalance.map(item => item.NM_PROPRIETARIO)))
            setSelectDistributor(UniqueValues.sort()[0])
            setDistributor(UniqueValues.sort())
        }
    }, [data, dataProduct, masterFilter])

    const {
        isOpen: isDialogOpen,
        onClose: onDialogClose,
        onOpen: onDialogOpen,
    } = useDisclosure()

    useEffect(() => {

        if (data) {
            const filteredData = masterFilter !== 'nenhum'
                ? data.filter(item => item.NM_PROPRIETARIO == selectDistributor && getTopicBalance(item) > 0)
                : data.filter(item => item.NM_PROPRIETARIO == selectDistributor)
            const UniqueValues = Array.from(new Set(filteredData.map(item => item.DD_ESTADO)))
            setSelectUFDistributor([UniqueValues.sort()[0]])
            setUFDistributor(UniqueValues.sort())
        }
    }, [selectDistributor, masterFilter])

    useEffect(() => {
        if (data && selectDistributor) {
            const revendasByDistributor = data
                .filter(item => item.NM_PROPRIETARIO === selectDistributor)
                .filter(item => masterFilter !== 'nenhum' ? getTopicBalance(item) > 0 : (item.TOTAL_EM_ABERTO || 0) > 0)
                .map(item => ({
                    value: item.DS_ROTA,
                    label: item.DS_ROTA
                }))
                .filter((value, index, self) =>
                    index === self.findIndex((t) => t.value === value.value)
                )
                .sort((a, b) => a.label.localeCompare(b.label))

            setRevendasList(revendasByDistributor)
            setSelectRevenda(revendasByDistributor.length > 0 ? [revendasByDistributor[0].value] : [])
        }
    }, [data, selectDistributor, masterFilter])

    return (
        <div className="flex flex-col h-full overflow-auto p-4">
            <TypographyH4>Dashboard</TypographyH4>
            <div className="flex gap-4 mb-2">
                <div className="mx-2 flex">
                    <TypographyMuted className="content-center mr-2">Tipo:</TypographyMuted>
                    <div className="w-[150px]">
                        <Select onValueChange={(value) => setMasterFilter(value as typeof masterFilter)} value={masterFilter}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="nenhum">Nenhum</SelectItem>
                                <SelectItem value="congelados">Congelados</SelectItem>
                                <SelectItem value="emAcordo">Em Acordo</SelectItem>
                                <SelectItem value="juridico">Jurídico</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="mx-2 flex">
                    <TypographyMuted className="content-center mr-2">Distribuidores:</TypographyMuted>
                    <div className="w-[200px]">
                        <Select onValueChange={(value) => setSelectDistributor(value)} value={selectDistributor}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="w-[220px]">
                                {distributor?.map((option, index) => (
                                    <SelectItem key={index} value={option}>{option}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="mx-2 flex">
                    <TypographyMuted className="content-center mr-2">UF:</TypographyMuted>
                    <div className="w-[140px]">

                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="justify-start w-full gap-2 text-xs"
                                >
                                    {selectUFDistributor?.length == 0 ? (
                                        <TypographyXS className="flex-1 text-start">
                                            Selecione uma UF
                                        </TypographyXS>
                                    ) : (
                                        <>
                                            <TypographyXS className="p-1 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                {selectUFDistributor.join(", ")}
                                            </TypographyXS>
                                        </>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="z-50 p-0" align="start">
                                <VirtualizedCommand
                                    options={UFDistributor.filter(Boolean).map(item => { return { value: item, label: item } })}
                                    placeholder="UF"
                                    onSelectionAllChange={() => {
                                        setSelectUFDistributor(UFDistributor)
                                    }}
                                    onClearFilters={() => { setSelectUFDistributor([]) }}
                                    selectedValues={
                                        selectUFDistributor?.reduce((acc, curr) => {
                                            return {
                                                ...acc,
                                                [curr]: true,
                                            }
                                        }, {} as Record<string, boolean>) ?? {}
                                    }
                                    onSelectionChange={(data) => {
                                        console.log(data)
                                        setSelectUFDistributor((prevItems) => {
                                            // Verifica se o item já existe no array
                                            if (prevItems?.includes(data)) {
                                                // Remove o item do array
                                                return prevItems?.filter((i) => i !== data);
                                            } else {
                                                // Adiciona o item ao array
                                                return [...prevItems, data];
                                            }
                                        })
                                    }}
                                />
                            </PopoverContent>
                        </Popover>

                    </div>
                </div>
                <div className="mx-2 flex">
                    <TypographyMuted className="content-center mr-2">Revenda:</TypographyMuted>
                    <div className="w-[200px]">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="justify-start w-full gap-2 text-xs"
                                >
                                    {selectRevenda?.length == 0 ? (
                                        <TypographyXS className="flex-1 text-start">
                                            Selecione uma Revenda
                                        </TypographyXS>
                                    ) : (
                                        <>
                                            <TypographyXS className="p-1 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                {selectRevenda.length} selecionada(s)
                                            </TypographyXS>
                                        </>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="z-50 p-0" align="start">
                                <VirtualizedCommand
                                    options={revendasList}
                                    placeholder="Revenda"
                                    onSelectionAllChange={() => {
                                        setSelectRevenda(revendasList.map(r => r.value))
                                    }}
                                    onClearFilters={() => { setSelectRevenda([]) }}
                                    selectedValues={
                                        selectRevenda?.reduce((acc, curr) => {
                                            return {
                                                ...acc,
                                                [curr]: true,
                                            }
                                        }, {} as Record<string, boolean>) ?? {}
                                    }
                                    onSelectionChange={(data) => {
                                        setSelectRevenda((prevItems) => {
                                            if (prevItems?.includes(data)) {
                                                return prevItems?.filter((i) => i !== data);
                                            } else {
                                                return [...prevItems, data];
                                            }
                                        })
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
                <div className="mx-2 flex gap-2">
                    <ButtonWithTooltip
                        tooltipContent={<p className="text-white">Visualizar PDF</p>}
                        variant={'ghost'}
                        onClick={() => onDialogOpen()}
                    >
                        <FileText size={18} />

                    </ButtonWithTooltip>
                </div>
                <ButtonWithTooltip
                    tooltipContent={<p className="text-white">Atualizar</p>}
                    onClick={() => { refetch(), refetchProduct() }}
                    variant="ghost"
                >
                    <RefreshCcw size={18} className={isFetching ? 'animate-spin ' : ''} />
                </ButtonWithTooltip>
            </div>

            {data && selectDistributor && selectUFDistributor && dataProduct && (
                <div className="flex flex-col gap-3 pb-4">
                    <div className="flex gap-3 min-w-0">
                        <div className="flex-1 min-w-0">
                            <Position1Dashboard data={data} dataProduct={dataProduct} distributor={selectDistributor} uf={selectUFDistributor} revenda={selectRevenda} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <Position2Dashboard data={data} dataProduct={dataProduct} distributor={selectDistributor} uf={selectUFDistributor} revenda={selectRevenda} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <Position3Dashboard data={data} dataProduct={dataProduct} distributor={selectDistributor} uf={selectUFDistributor} revenda={selectRevenda} />
                        </div>
                    </div>

                    <div className="flex rounded-xl bg-muted gap-3 min-w-0">
                        <PositionTotalDashboard data={data} dataProduct={dataProduct} distributor={selectDistributor} uf={selectUFDistributor} revenda={selectRevenda} />
                    </div>
                </div>
            )}
            {data && selectDistributor && selectUFDistributor && dataProduct && (
                <ViewPDF data={data} isOpen={isDialogOpen} onClose={onDialogClose} dataProduct={dataProduct} distributor={selectDistributor} uf={selectUFDistributor} />)
            }
        </div>
    )
}

export default Dashboard
